import React from 'react';
import { useTranslation } from 'react-i18next';

import cloneDeep from 'lodash.clonedeep';

import { AgGridReact, AgGridReactProps } from '@ag-grid-community/react';
import { ClientSideRowModelModule }  from "@ag-grid-community/client-side-row-model";
import { CsvExportModule }  from "@ag-grid-community/csv-export";
import { ModuleRegistry, PaginationChangedEvent } from '@ag-grid-community/core';

import Button from './Button';
import FlexRow from './FlexRow';
import Input from './Input';

/*interface GridFilterProps {
  model:				any;
  onModelChange:		any;
}

const GridFilter = (props: GridFilterProps) => {
  const filterChange = ({ target: { value: string }}) => onModelChange(value === '' ? null : value)
   
  return (
  <FlexRow className="px-[16px]"
           justify="center">
    <Input className="flex-01"
           type="text"
           value={model || ''}
           onChange={filterChange} />
    <span class="material-icons-outlined text-[16px] text-black">
      filter_alt
    </span>
  </FlexRow>
  );
}*/

const toolTip = (props: any): string => {
  const ret =	props.value
	  			?	props.value.toString()
	  			:	"";
  
  //console.log("Grid/toolTip/ret=" + ret + "/props=", props);
  
  return ret;
};

ModuleRegistry.registerModules([ ClientSideRowModelModule, CsvExportModule ]);

export type GridProps = AgGridReactProps & {
  pageSize?:				number;
  showPages?:				boolean;
  maxHeight?:				string;
  expander?:				string | boolean;
  expanderCaption?:			string;
  filter?:					boolean;
  globalFilter?:			boolean;
  csvFile?:					string;
  csvExport?:				number;
}

const defaultPageSize = 6;
//const pageSizes: number[] = [2, 4, 6, 10, 20, 30, 40, 50, 1000];

const Grid = (gprops: GridProps) => {
  const { t } = useTranslation();
  
  const {columnDefs, csvFile, csvExport, filter, globalFilter, maxHeight, pageSize, showPages, expander, expanderCaption, ...props} = gprops;
  
  const [pagesSize, setPagesSize] = React.useState(pageSize ? pageSize : defaultPageSize);
  const [pagesTotal, setPagesTotal] = React.useState(0);
  const [pagesCurrent, setPagesCurrent] = React.useState(0);
  const [pages, setPages] = React.useState([] as number[]);
  const [rowsCount, setRowsCount] = React.useState(0);
  const [quickFilter, setQuickFilter] = React.useState("");
  const [qfApplied, setQfApplied] = React.useState("");
  
  const [lastCsvExport, setLastCsvExport] = React.useState(csvExport ? csvExport : 0);
  
  const [pageSizeOpts] = React.useState([
    5, 10, 15, 20, 25, 30, 40, 50, 1000,
  ]);
  
  const [cols, setCols] = React.useState([] as any[]);

  const gridRef = React.useRef<any>();
  const containerRef = React.useRef<HTMLDivElement>(null);

  // ag-Grid's floating filter inputs are bare <input type="text"> elements.
  // Chrome happily autofills them with the saved login username / email,
  // which silently applies a column filter and hides all rows. autoComplete
  // alone isn't reliable (Chrome ignores it on text inputs), so we ALSO
  // toggle `readonly` on/off via focus — Chrome's autofill scanner skips
  // readonly fields entirely.
  React.useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    const harden = (el: HTMLInputElement) => {
      if (el.dataset.autofillHardened) return;
      el.dataset.autofillHardened = '1';
      el.setAttribute('autocomplete', 'off');
      el.setAttribute('autocorrect', 'off');
      el.setAttribute('spellcheck', 'false');
      el.setAttribute('data-lpignore', 'true');
      // Clear any value Chrome already autofilled before our handlers ran.
      if (el.value) {
        el.value = '';
        el.dispatchEvent(new Event('input', { bubbles: true }));
      }
      el.setAttribute('readonly', '');
      el.addEventListener('focus', () => el.removeAttribute('readonly'));
      el.addEventListener('blur',  () => el.setAttribute('readonly', ''));
    };
    const sweep = () => {
      // Only harden the floating filter inputs. Sweeping every <input>
      // also catches editable cell editors (e.g. Channel No.) and the
      // ticked checkbox, leaving them readonly so they reject all input.
      root.querySelectorAll('.ag-floating-filter input').forEach((el) => harden(el as HTMLInputElement));
    };
    sweep();
    const mo = new MutationObserver(sweep);
    mo.observe(root, { childList: true, subtree: true });
    return () => mo.disconnect();
  }, []);
  
  const hasExpander =	expander === true ||
  						expander === 'open' ||
  						expander === 'closed';
  
  const expanderDefault =	expander === true
  							?	true
  							:	(	expander === 'open'
  									?	true
  									:	false
  								);
  
  const gf =	globalFilter
  				?	true
  				:	false;
  
  const mh =	maxHeight === undefined
  				?	"calc(100vh - 220px)"
  				:	maxHeight;
  
  const pagesItemFirst = pagesCurrent * pagesSize + 1;
  const pagesItemLast = (pagesCurrent + 1) * pagesSize;
  
  const modules = React.useMemo(() => {
    return	csvFile
    		?	[ClientSideRowModelModule, CsvExportModule]
    		:	[ClientSideRowModelModule];
  }, [csvFile]);
  
  React.useEffect(() => {
    if (!columnDefs)
      return;
    
    if (!filter) {
      setCols(columnDefs);
      return
    }
    
    let columns: any[] = [];
    
    for (let col of columnDefs) {
      let cold: any = cloneDeep(col);
      
      if (!cold.hasOwnProperty("filter")) {
        cold.filter = 'agTextColumnFilter';
        cold.floatingFilter = true;
      }
      //cold.floatingFilterComponent = GridFilter;
      
      columns.push(cold);
    }
    
    setCols(columns);
    
    console.log("Grid/useEffect/filter=" + filter + "/columnDefs=", columnDefs, "/columns=", columns);
  }, [columnDefs, filter]);
  
  const exportCSV = React.useCallback(() => {
    console.log("Grid/exportCSV/csvFile=" + csvFile);
    gridRef.current.api.exportDataAsCsv({
      allColumns: true,
      fileName: csvFile
    });
  }, [gridRef.current, csvFile]);
  
  React.useEffect(() => {
    if (	!csvExport ||
    		csvExport == lastCsvExport
       )
      return;
    
    exportCSV();
  }, [csvExport, exportCSV]);
  
  const updatePages = (event: PaginationChangedEvent) => {
    if (!showPages)
      return;
    
    const pt = event.api.paginationGetTotalPages();
    const pc = event.api.paginationGetCurrentPage();
    const ps = event.api.paginationGetPageSize();
    const rc = event.api.paginationGetRowCount();
    
    console.log(
      "Grid/DeviceStatus/PaginationChangedEvent" +
      "/ps=" + ps +
      "/pagesSize=" + pagesSize +
      "/pt=" + pt +
      "/pagesTotal=" + pagesTotal +
      "/pc=" + pc +
      "/pagesCurrent=" + pagesCurrent +
      "/rc=" + rc +
      "/rowsCount=" + rowsCount +
      "/event=", event
    );
    
    if (pagesSize != ps)
      setPagesSize(ps);
    
    if (pt != pagesTotal)
      setPagesTotal(pt);
    
    if (pc != pagesCurrent)
      setPagesCurrent(pc);
    
    if (rc != rowsCount)
      setRowsCount(rc);
  };
  
  const updatePagesSize = (val: any) => {
    const ps = Number(val);
      
    if (ps != pagesSize)
      setPagesSize(ps);
  };
  
  const updatePagesCurrent = (pc: number) => {
    if (pc < 0)
      pc = 0;
    
    if (pc >= pagesTotal)
      pc = pagesTotal - 1;
    
    if (pagesCurrent != pc)
      gridRef.current.api.paginationGoToPage(pc);
  };
  
  React.useEffect(() => {
    const pags: number[] = [];
    
    for (let i=0; i<pagesTotal; i++) {
      if (	i > 2 &&
      		pagesTotal > 4 
         ) {
        i = Math.max(4, pagesTotal - 4);
        continue;
      }
      pags.push(i+1);
    }
    
    console.log(
      "Grid/useEffect" +
      "/pagesTotal=" + pagesTotal +
      "/pags=", pags
    );
    
    setPages(pags);
  }, [pagesTotal]);

  let lastPg = 0;
  
  return (
  <div ref={containerRef}
       style={{
	         width:			'100%',
             height:		mh,
             maxHeight:		mh,
           }}
           className="ag-theme-alpine [&_.ag-header]:z-[3] [&_.ag-floating-filter]:z-[3]">
  {gf &&
    <FlexRow className={[
               'bg-[--Light-Grey] px-[18px] py-[12px] gap-[10px]',
               'border-t-1 border-l-1 border-r-1 border-b-[--Lines]'
             ]}>
      <Input className="flex-01"
             background="bg-white"
             height="max-h-[24px]"
             placeholder={t('common:grid:quick_filter')}
             type="text"
             value={quickFilter}
             onValueChange={setQuickFilter}
             onEnterPressed={() => setQfApplied(quickFilter)} />
      <Button className="uppercase"
              caption={t('common:grid:apply')}
              onClick={() => setQfApplied(quickFilter)}
              layout="white" />
    </FlexRow>
  }
    <AgGridReact ref={gridRef}
                 //showGrid={true}
                 columnDefs={cols}
                 modules={modules}
                 tooltipMouseTrack={true}
                 tooltipShowDelay={500}
                 tooltipShowMode="whenTruncated"
                 tooltipTrigger="hover"
                 domLayout='normal'
                 headerHeight={38}
                 pagination={true}
                 defaultColDef={{
                   tooltipValueGetter:		toolTip,
                 }}
                 paginationPageSize={pagesSize}
                 paginationPageSizeSelector={pageSizeOpts}
                 quickFilterText={qfApplied}
                 suppressPaginationPanel={!showPages}
                 //onPaginationChanged={updatePages}
                 {...props} />
  </div>
  );
}

export const BooleanCell = (props: any) => {
  const { t } = useTranslation();
  
  const s =	props.value
		 	?	"common:fields:boolean:yes"
		 	:	"common:fields:boolean:no";
  
  //console.log("BooleanCell/s=" + s + "/props=", props);
  
  return (
  <FlexRow>
    { t(s) }
  </FlexRow>
  );
}

export default Grid;



